import React, { ChangeEvent } from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, flameTheme, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconClose } from '@lightspeed/flame/Icon/Close';
import { IconSearch } from '@lightspeed/flame/Icon/Search';
import { Input } from '@lightspeed/flame/Input';
import { ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { createReactRenderer } from '../helpers/react-renderer';

import {
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
  UnstyledButton,
} from './SerializedRefundModal.style';
import SerialNumberRow from './SerializedRefundModalSerialRow';
import { SerializedItem } from './types';

export const NO_RESULTS = 'No serial numbers found.';

export const SerializedRefundModal: React.FC<{
  saleId: number;
  transactionLineId: number;
  itemId: number;
  itemDescription: string;
  serials: SerializedItem[];
}> = ({ saleId, transactionLineId, itemId, itemDescription, serials }) => {
  const { colors } = flameTheme;

  const [modalOpen, setModalOpen] = React.useState(true);
  const [inputValue, setInputValue] = React.useState('');
  const [serialsList, setSerialsList] = React.useState(serials);

  const stringifiedSerialList = JSON.stringify(serialsList);
  const filteredSerialsList = React.useMemo(
    () =>
      serialsList.filter((serialObj) =>
        serialObj.serial.toLowerCase().includes(inputValue.toLowerCase().trim())
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue, stringifiedSerialList]
  );

  const isSelectingFromFilteredList = !!inputValue.trim();
  const hasZeroFilteredResults = !filteredSerialsList.length;

  const isAllSerialNumbersSelected = !!serialsList?.every(
    (serial) => serial.isSelected || serial.isRefunded
  );
  const isAllFilteredSerialSelected = !!filteredSerialsList?.every(
    (serial) => serial.isSelected || serial.isRefunded
  );
  const allSelectedSerials = serialsList.filter((serialObj) => serialObj.isSelected);

  const addRemoveItem = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>, serializedId: number) => {
      const { checked } = e.target;
      setSerialsList((prev) =>
        prev?.map((serial) => {
          const newSelection = serializedId === serial.serializedId ? checked : serial.isSelected;
          return { ...serial, isSelected: newSelection };
        })
      );
    },
    []
  );

  const handleAddRemoveAll = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    if (isSelectingFromFilteredList) {
      setSerialsList((prev) =>
        prev?.map((serial) => ({
          ...serial,
          isSelected:
            !serial.isRefunded && filteredSerialsList.includes(serial)
              ? checked
              : serial.isSelected,
        }))
      );
    } else {
      setSerialsList((prev) =>
        prev?.map((serial) => ({ ...serial, isSelected: serial.isRefunded ? false : checked }))
      );
    }
  };

  const handleRefundClick = () => {
    const allSelectedSerialsList = allSelectedSerials
      .map(({ serializedId }) => serializedId)
      .join(',');

    window.merchantos.utils.openUrl(
      `register.php?form_name=refund_transaction_line&transaction_line_id=${transactionLineId}&item_id=${itemId}&transaction_id=${saleId}&serialized_ids=${allSelectedSerialsList}`
    );

    setModalOpen(false);
  };

  const getTableHeaderLabel = () => {
    const fullSerialsListLength = serialsList.length;
    const filteredSerialsListLength = filteredSerialsList.length;

    if (inputValue && filteredSerialsListLength) {
      return (
        <Text>
          Results <var className="notranslate">({filteredSerialsListLength})</var>
        </Text>
      );
    }

    if (isAllSerialNumbersSelected) {
      // if ALL serials in the list are selected it will show: "All serial numbers (# selected)"
      return (
        <Text>
          All serial numbers (<var className="notranslate">{allSelectedSerials.length}</var>{' '}
          selected)
        </Text>
      );
    }

    if (serialsList?.some((serial) => serial.isSelected)) {
      // once selection(s) have been made it will change to show the number of serials selected: "Serial numbers (# selected)"
      return (
        <Text>
          Serial numbers (<var className="notranslate">{allSelectedSerials.length}</var> selected)
        </Text>
      );
    }

    // by default no serials are selected and table header will show the number of serials listed (including all previously refunded and selected serials): "Serial numbers (#)"
    return (
      <Text>
        Serial numbers <var>({fullSerialsListLength})</var>
      </Text>
    );
  };

  // eslint-disable-next-line consistent-return
  const getSearchInputSuffix = () => {
    if (inputValue)
      return (
        <UnstyledButton data-test="clear-search-input-value" onClick={() => setInputValue('')}>
          <IconClose color={colors.white} detailsColor="#CCCCCC" />
        </UnstyledButton>
      );
  };

  const renderSerialsList = () => {
    return filteredSerialsList.map(({ serial, serializedId, isSelected, isRefunded }) => (
      <SerialNumberRow
        serial={serial}
        isSelected={isSelected}
        isRefunded={isRefunded}
        key={serializedId}
        serializedId={serializedId}
        onCheckClick={(e) => addRemoveItem(e, serializedId)}
      />
    ));
  };

  const isSerialsListUnchanged = () => JSON.stringify(serials) === JSON.stringify(serialsList);

  const isSelectAllIntermediateState = isSelectingFromFilteredList
    ? !!filteredSerialsList?.some((serial) => serial.isSelected || serial.isRefunded)
    : !!serialsList?.some((serial) => serial.isSelected || serial.isRefunded);

  return (
    <div>
      <StyledModal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)}>
        <ModalHeader>
          Refund{' '}
          <span className="notranslate" data-test="item-description">
            &quot;{itemDescription}&quot;
          </span>
        </ModalHeader>
        <Flex flexDirection="column" width="100%">
          <Box p={3}>
            <Input
              value={inputValue}
              autoFocus
              width="100%"
              style={{ paddingLeft: '8px' }}
              onChange={(e) => setInputValue(e.target.value)}
              data-test="serialized-refund-search-filter"
              placeholder="Search a serial"
              prefix={<IconSearch size="small" color={colors['gray-950']} />}
              suffix={getSearchInputSuffix()}
            />
          </Box>
          <Divider color={colors['gray-100']} />
          <Box p={3} data-test="table-header">
            {hasZeroFilteredResults ? (
              <Text color="#8B939B">{NO_RESULTS}</Text>
            ) : (
              <Checkbox
                id="select-all-checkbox"
                data-test="select-all-checkbox"
                label={getTableHeaderLabel()}
                indeterminate={isSelectAllIntermediateState}
                checked={
                  isSelectingFromFilteredList
                    ? isAllFilteredSerialSelected
                    : isAllSerialNumbersSelected
                }
                onChange={handleAddRemoveAll}
              />
            )}
          </Box>
        </Flex>
        <Divider />
        <StyledModalBody>
          <Flex flexDirection="column">{renderSerialsList()}</Flex>
        </StyledModalBody>
        <StyledModalFooter>
          <Flex justifyContent="flex-end" alignItems="center">
            <Button
              onClick={() => setModalOpen(false)}
              mr={2}
              data-test="cancel-print-gift-receipt-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefundClick}
              disabled={isSerialsListUnchanged()}
              variant="secondary"
              fill
              data-test="refund-serialized-items-button"
            >
              Refund
            </Button>
          </Flex>
        </StyledModalFooter>
      </StyledModal>
    </div>
  );
};

export const openSerializedRefundModal = ({
  transactionLineId,
  saleId,
  itemId,
  itemDescription,
  serials,
}: {
  transactionLineId: number;
  saleId: number;
  itemId: number;
  itemDescription: string;
  serials: SerializedItem[];
}) => {
  const RenderedComponent = createReactRenderer({
    component: SerializedRefundModal,
    selector: '[data-react-root="ReactModal"]',
  });
  RenderedComponent.mount({
    props: { transactionLineId, saleId, itemId, itemDescription, serials },
  });
};
